// Points of Interest the IVR's address matching leans on for local
// landmarks generic geocoding gets wrong (e.g. "Hospital Main Entry" only
// geocodes to the helipad in Google Maps). Seeded once from a CSV export
// of the IVR's own POI list (migration 0052) -- this screen is where
// Janelle can browse/search it, fix a bad coordinate, or add a new place
// the IVR keeps mishearing/mismatching, without going back to a spreadsheet.
import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { Plus, RefreshCw, Trash2, Pencil, MapPin, Info } from "lucide-react";
import { IvrPoi, fetchIvrPois, createIvrPoi, updateIvrPoi, deleteIvrPoi } from "@/lib/ivrPois";

const emptyForm = {
  name: "",
  lon: "",
  lat: "",
  cat: "0",
  zid: "0",
  flags: "0",
  notes: "",
};

const IvrPoiSettings: React.FC = () => {
  const [pois, setPois] = useState<IvrPoi[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  const [editing, setEditing] = useState<IvrPoi | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    setPois(await fetchIvrPois());
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return pois.filter((p) => {
      if (!showInactive && !p.is_active) return false;
      if (!term) return true;
      return p.name.toLowerCase().includes(term);
    });
  }, [pois, search, showInactive]);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (poi: IvrPoi) => {
    setEditing(poi);
    setForm({
      name: poi.name,
      lon: String(poi.lon),
      lat: String(poi.lat),
      cat: String(poi.cat),
      zid: String(poi.zid),
      flags: String(poi.flags),
      notes: poi.notes || "",
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.lon.trim() || !form.lat.trim()) {
      toast({ title: "Error", description: "Name, longitude and latitude are required", variant: "destructive" });
      return;
    }
    const lon = parseFloat(form.lon);
    const lat = parseFloat(form.lat);
    if (Number.isNaN(lon) || Number.isNaN(lat)) {
      toast({ title: "Error", description: "Longitude/latitude must be numbers", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        lon,
        lat,
        cat: parseInt(form.cat, 10) || 0,
        zid: parseInt(form.zid, 10) || 0,
        flags: parseInt(form.flags, 10) || 0,
        notes: form.notes.trim() || null,
      };
      if (editing) {
        await updateIvrPoi(editing.id, payload);
        toast({ title: "Saved", description: "POI updated." });
      } else {
        await createIvrPoi(payload);
        toast({ title: "Added", description: "POI created." });
      }
      setShowForm(false);
      load();
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
    setSaving(false);
  };

  const toggleActive = async (poi: IvrPoi) => {
    try {
      await updateIvrPoi(poi.id, { is_active: !poi.is_active });
      setPois((prev) => prev.map((p) => (p.id === poi.id ? { ...p, is_active: !p.is_active } : p)));
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
  };

  const remove = async (poi: IvrPoi) => {
    if (!window.confirm(`Delete "${poi.name}"? This can't be undone.`)) return;
    try {
      await deleteIvrPoi(poi.id);
      setPois((prev) => prev.filter((p) => p.id !== poi.id));
      toast({ title: "Deleted", description: `"${poi.name}" removed.` });
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
  };

  return (
    <Card className="shadow-lg bg-card border border-border">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-lg text-primary flex items-center gap-2">
          <MapPin className="w-5 h-5" />
          Points of Interest
        </CardTitle>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button size="sm" onClick={openAdd} className="golden-button">
            <Plus className="w-4 h-4 mr-2" />
            Add POI
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-start gap-2 rounded-lg border border-blue-900/40 bg-blue-950/20 p-3 mb-4">
          <Info className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
          <p className="text-xs text-blue-200">
            Local landmarks the IVR matches a caller's spoken place to when generic geocoding gets it wrong (e.g.
            hospital gate entrances, cab stands). "Cat" and "Zid" are the IVR system's own category/zone codes --
            kept as-is here rather than relabelled, since their exact meaning lives on the IVR side.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name..." className="max-w-xs" />
          <div className="flex items-center gap-2">
            <Checkbox id="show-inactive-pois" checked={showInactive} onCheckedChange={(c) => setShowInactive(!!c)} />
            <Label htmlFor="show-inactive-pois" className="text-sm cursor-pointer">
              Show inactive
            </Label>
          </div>
          <span className="text-xs text-muted-foreground ml-auto">
            {filtered.length} of {pois.length} POIs
          </span>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground">No POIs found.</p>
        ) : (
          <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Lon</TableHead>
                  <TableHead>Lat</TableHead>
                  <TableHead>Cat</TableHead>
                  <TableHead>Zid</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((poi) => (
                  <TableRow key={poi.id} className={!poi.is_active ? "opacity-50" : ""}>
                    <TableCell className="font-medium">{poi.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{poi.lon}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{poi.lat}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{poi.cat}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{poi.zid}</Badge>
                    </TableCell>
                    <TableCell>
                      <Checkbox checked={poi.is_active} onCheckedChange={() => toggleActive(poi)} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(poi)}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => remove(poi)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit POI" : "Add POI"}</DialogTitle>
            <DialogDescription>
              {editing ? `Editing "${editing.name}"` : "New point of interest for the IVR's address matching."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Hospital Main Entry" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Longitude *</Label>
                <Input value={form.lon} onChange={(e) => setForm((f) => ({ ...f, lon: e.target.value }))} placeholder="175.620843" />
              </div>
              <div>
                <Label>Latitude *</Label>
                <Input value={form.lat} onChange={(e) => setForm((f) => ({ ...f, lat: e.target.value }))} placeholder="-40.338485" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Cat</Label>
                <Input type="number" value={form.cat} onChange={(e) => setForm((f) => ({ ...f, cat: e.target.value }))} />
              </div>
              <div>
                <Label>Zid</Label>
                <Input type="number" value={form.zid} onChange={(e) => setForm((f) => ({ ...f, zid: e.target.value }))} />
              </div>
              <div>
                <Label>Flags</Label>
                <Input type="number" value={form.flags} onChange={(e) => setForm((f) => ({ ...f, flags: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="e.g. Google only geocodes this to the helipad -- use this pin instead"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="golden-button">
              {saving ? "Saving..." : editing ? "Save Changes" : "Add POI"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default IvrPoiSettings;
